import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

function PersonAvatar({ name, imagePath }) {
  const [err, setErr] = useState(false);
  const initials = (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  if (!err && imagePath) {
    return (
      <img src={getImageURL(imagePath)} alt={name} onError={() => setErr(true)}
        className="w-7 h-7 rounded-full shrink-0 object-cover border border-gray-200 shadow-sm" />
    );
  }
  return (
    <div className="w-7 h-7 rounded-full shrink-0 border border-gray-200 shadow-sm bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center">
      <span className="font-bold text-blue-700 text-[9px] leading-none">{initials}</span>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
    </div>
  );
}

// Confirms which batch the student is ACTUALLY following before removing the
// other one -- the whole point is not to silently delete the wrong side of a
// duplicate, so the remaining registration(s) are shown alongside the one
// about to be removed.
function RemoveRegistrationModal({ student, registration, remaining, removing, error, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="rounded-t-2xl bg-red-600 px-5 py-4">
          <h3 className="text-base font-bold text-white">Remove duplicate registration?</h3>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Removing <span className="font-semibold text-gray-900">{student.studentName}</span>'s registration in:
          </p>
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-sm font-semibold text-red-800">{registration.batchLabel}</p>
            <p className="text-xs text-red-600">Reg. No. {registration.registrationNumber || '—'} · Course ID {registration.courseId}</p>
          </div>

          <p className="text-sm text-gray-600">
            Confirm the student's <span className="font-semibold">actual, currently-following batch</span> — this will remain after removal:
          </p>
          <div className="space-y-2">
            {remaining.map((r) => (
              <div key={r.registrationId} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-sm font-semibold text-emerald-800">{r.batchLabel}</p>
                <p className="text-xs text-emerald-600">Reg. No. {r.registrationNumber || '—'} · Course ID {r.courseId}</p>
              </div>
            ))}
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <p className="text-xs text-gray-400">This deletes the registration record. It cannot be undone.</p>
        </div>
        <div className="flex justify-end gap-3 rounded-b-2xl bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={removing}
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={removing}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:bg-red-700 disabled:opacity-50"
          >
            {removing ? 'Removing…' : 'Confirm Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Students grouped by course NAME (courses sharing a name but different
// courseIds are merged into one group), with any student holding an active
// registration in 2+ batches inside that group flagged as a duplicate.
export default function DuplicateCourseRegistrationsView() {
  const [courseGroups, setCourseGroups] = useState([]);
  const [totalDuplicateStudents, setTotalDuplicateStudents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyDuplicates, setOnlyDuplicates] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null); // { student, registration, remaining }
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  useEffect(() => {
    axiosInstance.get('/admin/me/coordinate-courses')
      .then((r) => setIsSuperAdmin(Boolean(r.data?.isSuperAdmin)))
      .catch(() => setIsSuperAdmin(false));
  }, []);

  const load = () => {
    setLoading(true);
    setError(null);
    axiosInstance.get('/admin/coordinator-reports/duplicate-course-registrations')
      .then((r) => {
        const groups = r.data.courseGroups || [];
        setCourseGroups(groups);
        setTotalDuplicateStudents(r.data.totalDuplicateStudents || 0);
        setExpanded(new Set(groups.filter((g) => g.duplicateCount > 0).map((g) => g.courseName)));
      })
      .catch(() => setError('Failed to load student course report.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggle = (name) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const openRemoveModal = (student, registration) => {
    const remaining = student.registrations.filter((r) => r.registrationId !== registration.registrationId);
    setRemoveError(null);
    setPendingRemoval({ student, registration, remaining });
  };

  const confirmRemove = async () => {
    if (!pendingRemoval) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await axiosInstance.delete(`/admin/registrations/${pendingRemoval.registration.registrationId}`);
      setPendingRemoval(null);
      load();
    } catch (err) {
      setRemoveError(err?.response?.data?.message || 'Failed to remove this registration.');
    } finally {
      setRemoving(false);
    }
  };

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return courseGroups
      .map((g) => {
        let students = g.students;
        if (onlyDuplicates) students = students.filter((s) => s.duplicate);
        if (q) {
          students = students.filter(
            (s) =>
              s.studentName.toLowerCase().includes(q) ||
              (s.studentId || '').toLowerCase().includes(q) ||
              (s.telephone || '').toLowerCase().includes(q) ||
              s.registrations.some((r) => (r.registrationNumber || '').toLowerCase().includes(q))
          );
        }
        return { ...g, students };
      })
      .filter((g) => g.students.length > 0);
  }, [courseGroups, search, onlyDuplicates]);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
            {totalDuplicateStudents > 0
              ? `${totalDuplicateStudents} duplicate registration${totalDuplicateStudents === 1 ? '' : 's'} found`
              : 'No duplicate registrations found'}
          </h2>
          <p className="mt-1 text-xs text-indigo-700">
            Students grouped by course name — courses that share a name but have different course IDs are merged into one group,
            so a student registered in a batch under each looks like two separate courses but is really the same one.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-indigo-800">
            <input
              type="checkbox"
              checked={onlyDuplicates}
              onChange={(e) => setOnlyDuplicates(e.target.checked)}
              className="h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
            />
            Only show duplicates
          </label>
          <button
            type="button"
            onClick={load}
            className="rounded-lg border border-indigo-600 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      <input
        type="text"
        placeholder="Search student name, student ID, mobile/telephone, or registration number…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-sm rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />

      {filteredGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-12 h-12 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm">{onlyDuplicates ? 'No duplicate registrations found.' : 'No students found.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredGroups.map((g) => {
            const isOpen = expanded.has(g.courseName);
            return (
              <div key={g.courseName} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <button type="button" onClick={() => toggle(g.courseName)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="min-w-0 text-left">
                    <p className="text-sm font-semibold text-gray-900 truncate">{g.courseName}</p>
                    <p className="text-xs text-gray-400">
                      {g.courseIds.length > 1 ? `${g.courseIds.length} course IDs sharing this name · ` : ''}
                      {g.studentCount} student{g.studentCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    {g.duplicateCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">
                        {g.duplicateCount} duplicate{g.duplicateCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead>
                        <tr className="bg-gray-50">
                          {['Student', 'Student ID', 'Mobile / Telephone', 'Batches Registered'].map((h) => (
                            <th key={h} className="px-4 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {g.students.map((s) => (
                          <tr key={s.studentId} className={s.duplicate ? 'bg-red-50/50' : 'hover:bg-gray-50/50'}>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <PersonAvatar name={s.studentName} imagePath={s.imagePath} />
                                <span className="text-sm font-medium text-gray-800">{s.studentName}</span>
                                {s.duplicate && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Duplicate</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{s.studentId || '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{s.telephone || '—'}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex flex-col gap-1">
                                {s.registrations.map((r) => (
                                  <div key={r.registrationId} className="flex items-center gap-2">
                                    <span className="text-xs text-gray-700">
                                      {r.batchLabel} <span className="text-gray-400">· Reg. No. {r.registrationNumber || '—'} · Course ID {r.courseId}</span>
                                    </span>
                                    {isSuperAdmin && s.duplicate && (
                                      <button
                                        type="button"
                                        onClick={() => openRemoveModal(s, r)}
                                        title="Remove this registration"
                                        className="shrink-0 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-100 transition-colors"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pendingRemoval && (
        <RemoveRegistrationModal
          student={pendingRemoval.student}
          registration={pendingRemoval.registration}
          remaining={pendingRemoval.remaining}
          removing={removing}
          error={removeError}
          onCancel={() => { if (!removing) { setPendingRemoval(null); setRemoveError(null); } }}
          onConfirm={confirmRemove}
        />
      )}
    </div>
  );
}
